import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import EigooMark from "../components/EigooMark";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { DIRECTORATE, LEGAL_ENTITY } from "../lib/directorate";
import {
    Anchor,
    Lock,
    Crown,
    MapPin,
    Banknote,
    LineChart,
    ScrollText,
    Mail,
    ArrowLeft,
} from "lucide-react";

const cormorant = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

const PROPERTY = {
    code: "FOUNDRY-LISTING-01",
    title: "Foundry Listing 01 · The Anchor Estate",
    location: "Coastal flagship · Director-matched portfolio",
    asset_class: "Sovereign Real Estate · Mixed-use",
    target_raise_usd: 4_500_000,
    minimum_partner_usd: 50_000,
    structure: "Founding Privateer SPV · 60/30/10 yield covenant",
    horizon: "5-7 years · perpetual residual on completion",
    bond_status: "By invitation · NDA-gated",
    snapshot: [
        { label: "Anchor yield (target)", value: "11.4% IRR" },
        { label: "Cash-on-cash (year 3)", value: "8.2%" },
        { label: "Hold period", value: "5-7 yrs" },
        { label: "Founding seats", value: "12 of 12 reserved" },
    ],
    thesis: [
        "Recasts a stalled coastal flagship into the first SPF anchor estate.",
        "Operates under the Sovereign 60/30/10 — operatives earn 60%, Fleet 30%, CyberKids Anchor 10%.",
        "Closes the Sovereign covenant: every Founding Privateer holds perpetual residual rights.",
        "Director-led capital deployment with quarterly transparency deliveries through the Bridge.",
    ],
};

const fmt$ = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export default function FoundryListing() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [capital, setCapital] = useState("");
    const [busy, setBusy] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Director-only privacy gate (clients land here only via NDA-gated link).
    const isCommissioned = user && (user.role === "director" || user.role === "founding_privateer");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/waitlist", {
                tenant: "spf",
                email,
                meta: `name=${name} | property=${PROPERTY.code} | capital=${capital || "n/a"} | routed_to=${DIRECTORATE.director.alias_email}`,
            });
            setSubmitted(true);
            toast.success("Brief filed with the Director's office.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-background grain-overlay relative" data-testid="foundry-listing">
            {/* Celestial wash */}
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-30">
                <div className="absolute top-[-20%] right-[-10%] w-[55%] h-[55%] rounded-full bg-[hsl(40_60%_45%/0.10)] blur-[120px]" />
                <div className="absolute bottom-[-20%] left-[-10%] w-[55%] h-[55%] rounded-full bg-[hsl(153_50%_25%/0.20)] blur-[120px]" />
            </div>

            <header className="relative z-10 max-w-6xl mx-auto px-6 py-6 flex items-center justify-between flex-wrap gap-4">
                <EigooMark size="lg" sub="Sovereign Property Foundry" testid="spf-mark" />
                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em]">
                    <button
                        onClick={() => navigate(-1)}
                        data-testid="spf-back"
                        className="text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors flex items-center gap-1.5"
                    >
                        <ArrowLeft size={14} /> Back
                    </button>
                    <Link to="/login" className="text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors" data-testid="spf-login">
                        Bridge Login
                    </Link>
                </div>
            </header>

            <main className="relative z-10 max-w-6xl mx-auto px-6 pb-20">
                {/* Privacy banner for unauth visitors */}
                {!isCommissioned && (
                    <div className="rounded-xl border border-[hsl(40_50%_40%/0.4)] bg-[hsl(40_50%_18%/0.18)] p-4 mb-8 flex items-start gap-3" data-testid="spf-nda-banner">
                        <Lock size={16} className="text-[hsl(45_85%_68%)] mt-1 shrink-0" />
                        <div className="text-xs text-foreground/85">
                            <span className="font-semibold text-[hsl(45_85%_75%)]">NDA-gated.</span> Property briefs in
                            the Sovereign Property Foundry are surfaced to vetted Founding Privateers and Director-invited
                            investors only. Submitting your contact below routes your file to {" "}
                            <span className="font-mono text-[hsl(45_85%_68%)]">{DIRECTORATE.director.alias_email}</span> for review.
                        </div>
                    </div>
                )}

                {/* Hero */}
                <section className="grid lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 glass rounded-3xl p-8 border border-[hsl(40_40%_25%/0.3)] relative overflow-hidden">
                        <div className="text-[10px] uppercase tracking-[0.4em] text-[hsl(45_85%_68%)] mb-4 flex items-center gap-2">
                            <Crown size={11} /> {DIRECTORATE.spf.division} · {PROPERTY.code}
                        </div>
                        <h1 className="font-heading text-5xl sm:text-6xl tracking-tighter leading-[0.95]">
                            {PROPERTY.title}
                        </h1>
                        <p
                            className="mt-6 text-xl text-foreground/85 leading-relaxed max-w-2xl"
                            style={{ ...cormorant, fontStyle: "italic" }}
                        >
                            "{DIRECTORATE.spf.proclamation}"
                        </p>
                        <div className="mt-2 text-[10px] uppercase tracking-[0.35em] text-[hsl(45_85%_68%)]">
                            — {DIRECTORATE.spf.signatory_name}, {DIRECTORATE.spf.signatory_title}
                        </div>

                        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {PROPERTY.snapshot.map((s) => (
                                <div key={s.label} className="rounded-xl border border-[hsl(40_40%_25%/0.3)] p-3">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{s.label}</div>
                                    <div className="font-heading text-xl mt-1 gold-gradient-text">{s.value}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Sidecar — quick facts */}
                    <aside className="glass rounded-3xl p-6 border border-[hsl(40_40%_25%/0.3)] space-y-4 text-sm" data-testid="spf-facts">
                        <Fact icon={MapPin} label="Location">{PROPERTY.location}</Fact>
                        <Fact icon={LineChart} label="Asset class">{PROPERTY.asset_class}</Fact>
                        <Fact icon={Banknote} label="Target raise">{fmt$(PROPERTY.target_raise_usd)}</Fact>
                        <Fact icon={Anchor} label="Min. partner stake">{fmt$(PROPERTY.minimum_partner_usd)}</Fact>
                        <Fact icon={ScrollText} label="Structure">{PROPERTY.structure}</Fact>
                        <Fact icon={Lock} label="Bond status">{PROPERTY.bond_status}</Fact>
                    </aside>
                </section>

                {/* Thesis */}
                <section className="mt-10 glass rounded-3xl p-8 border border-[hsl(40_40%_25%/0.3)]">
                    <div className="text-[10px] uppercase tracking-[0.35em] text-[hsl(45_85%_68%)] mb-4">Director's thesis</div>
                    <ul className="space-y-3">
                        {PROPERTY.thesis.map((t, i) => (
                            <li key={i} className="flex items-start gap-3 text-base text-foreground/85">
                                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[hsl(45_85%_68%)] shrink-0" />
                                <span>{t}</span>
                            </li>
                        ))}
                    </ul>
                </section>

                {/* Investor brief intake */}
                <section className="mt-10 grid lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 glass rounded-3xl p-8 border border-[hsl(40_40%_25%/0.3)]">
                        <div className="text-[10px] uppercase tracking-[0.35em] text-[hsl(45_85%_68%)] mb-3">Investor brief intake</div>
                        <h2 className="font-heading text-3xl tracking-tight">Request the Director's brief.</h2>
                        <p className="text-sm text-muted-foreground mt-2 max-w-xl">
                            File your contact and indicative capital. {DIRECTORATE.director.name}'s office reviews briefs
                            within one business day and routes a follow-up through {" "}
                            <span className="font-mono text-[hsl(45_85%_68%)]">{DIRECTORATE.director.alias_email}</span>.
                        </p>

                        {submitted ? (
                            <div className="mt-6 rounded-xl border border-[hsl(45_85%_68%/0.5)] bg-[hsl(40_60%_18%/0.4)] p-5" data-testid="spf-submitted">
                                <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_68%)]">Brief received</div>
                                <div className="font-heading text-xl mt-1">Signal locked. Director's office takes the line.</div>
                                <p className="text-xs text-muted-foreground mt-2">
                                    Expect contact within one business day from {" "}
                                    <span className="font-mono">{DIRECTORATE.director.alias_email}</span>.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="spf-form">
                                <div className="sm:col-span-2">
                                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Name</label>
                                    <input
                                        required
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        data-testid="spf-name"
                                        className="mt-1 w-full px-4 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)]"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Email</label>
                                    <input
                                        required
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        data-testid="spf-email"
                                        className="mt-1 w-full px-4 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)]"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Indicative capital (USD)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="10000"
                                        placeholder={`min ${fmt$(PROPERTY.minimum_partner_usd)}`}
                                        value={capital}
                                        onChange={(e) => setCapital(e.target.value)}
                                        data-testid="spf-capital"
                                        className="mt-1 w-full px-4 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)]"
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <button
                                        type="submit"
                                        disabled={busy}
                                        data-testid="spf-submit"
                                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-50"
                                    >
                                        <Mail size={16} /> {busy ? "Routing brief..." : "Route to Director's Office"}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>

                    {/* Signatory block */}
                    <aside className="glass rounded-3xl p-6 border border-[hsl(40_40%_25%/0.3)] flex flex-col" data-testid="spf-signature">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_68%)]">Signatory</div>
                        <div className="font-heading text-3xl mt-2 tracking-tight gold-gradient-text">
                            {DIRECTORATE.spf.signatory_name}
                        </div>
                        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mt-1">
                            {DIRECTORATE.spf.signatory_title}
                        </div>
                        <p
                            className="mt-5 text-base text-foreground/75 leading-relaxed"
                            style={{ ...cormorant, fontStyle: "italic" }}
                        >
                            "Real estate is where the fleet drops anchor in the void.
                            We don't sell properties — we underwrite charters."
                        </p>
                        <div className="mt-6 pt-4 border-t border-[hsl(40_40%_25%/0.18)] text-xs text-muted-foreground">
                            <div className="flex items-center gap-2">
                                <Mail size={12} className="text-[hsl(45_85%_68%)]" />
                                <a
                                    href={`mailto:${DIRECTORATE.director.alias_email}`}
                                    className="font-mono text-[hsl(45_85%_75%)] hover:text-[hsl(45_85%_92%)]"
                                    data-testid="spf-alias-email"
                                >
                                    {DIRECTORATE.director.alias_email}
                                </a>
                            </div>
                            <div className="text-[10px] mt-2 opacity-70">
                                Routes to {DIRECTORATE.director.sales_hub_email} · DNS alias active
                            </div>
                            {/* Coordinate of record — sovereign property division */}
                            <div
                                data-testid="spf-coordinate"
                                className="mt-3 pt-3 border-t border-[hsl(40_40%_25%/0.18)] space-y-0.5"
                            >
                                <div className="text-[9px] uppercase tracking-[0.35em] text-[hsl(45_85%_68%)] font-mono">
                                    Coordinate of record
                                </div>
                                {LEGAL_ENTITY.address_block.map((line, i) => (
                                    <div
                                        key={i}
                                        className={`font-mono text-[11px] ${
                                            i === 0
                                                ? "text-[hsl(45_85%_75%)]"
                                                : "text-foreground/70"
                                        }`}
                                    >
                                        {line}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </aside>
                </section>
            </main>
        </div>
    );
}

function Fact({ icon: Icon, label, children }) {
    return (
        <div className="flex items-start gap-3">
            <Icon size={16} className="text-[hsl(45_85%_68%)] mt-0.5 shrink-0" />
            <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</div>
                <div className="text-foreground/85 mt-0.5">{children}</div>
            </div>
        </div>
    );
}
